import React, { useState, useEffect } from 'react';
import { clientsApi, auditsApi } from '../services/api';
import { User } from '../contexts/AuthContext';

interface ClientAuditsListProps {
  user: User;
  clientId?: number; // ID específico de cliente a filtrar (para admin grupos)
  showOnlyDelivered?: boolean; // Solo mostrar auditorías recibidas (para "Mis Auditorías")
  showOnlyPending?: boolean; // Solo mostrar auditorías pendientes (para "Mis Pedidos")
  onEditAudit?: (auditId: number) => void; // Callback para editar auditoría
}

interface Audit {
  id: number;
  titulo: string;
  estado: string;
  fechaSolicitada: string;
  fechaTerminacion?: string;
  fechaAprobacion?: string;
  operadorNombre?: string;
  observacionesSupervisor?: string;
  archivoWord?: string;
  sucursal?: string;
  costoFinal?: number;
  estadoPago?: string;
  branch?: {
    id: number;
    nombre: string;
  };
}

const ClientAuditsList: React.FC<ClientAuditsListProps> = ({ user, clientId, showOnlyDelivered = false, showOnlyPending = false, onEditAudit }) => {
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClientAudits();
  }, [clientId]); // Se recarga cuando cambia clientId (incluyendo undefined)

  const loadClientAudits = async () => {
    try {
      setLoading(true);
      let response;

      if (clientId) {
        // Si se especifica un clientId, cargar auditorías de ese cliente específico
        response = await clientsApi.getAudits(clientId);
      } else {
        // Comportamiento normal - cargar auditorías del usuario actual
        response = await clientsApi.getMyAudits();
      }

      // Intentar diferentes formatos de respuesta para compatibilidad
      const auditorias = Array.isArray(response.data?.data) ? response.data.data :
                        (Array.isArray(response.data) ? response.data : []);
      setAudits(auditorias);
    } catch (error) {
      console.error('Error cargando auditorías del cliente:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAudit = async (auditId: number) => {
    if (!window.confirm('¿Estás seguro de eliminar esta auditoría? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await auditsApi.delete(auditId);
      alert('Auditoría eliminada exitosamente');
      loadClientAudits();
    } catch (error: any) {
      console.error('Error eliminando auditoría:', error);
      alert('Error al eliminar la auditoría: ' + (error.response?.data?.error || error.message));
    }
  };

  // Separar en pedidos y recibidas
  const misPedidos = audits.filter(a => ['pendiente', 'asignada', 'en_proceso', 'pausada'].includes(a.estado));
  const recibidas = audits.filter(a => ['terminada', 'entregada'].includes(a.estado));

  const getEstadoBadge = (estado: string) => {
    const estilos: any = {
      'pendiente': { bg: '#fef3c7', color: '#92400e', text: '⏳ Pendiente' },
      'asignada': { bg: '#dbeafe', color: '#1e40af', text: '👤 Asignada' },
      'en_proceso': { bg: '#fde68a', color: '#92400e', text: '🔄 En Proceso' },
      'terminada': { bg: '#d1fae5', color: '#065f46', text: '✅ Completada' },
      'entregada': { bg: '#d1fae5', color: '#065f46', text: '✅ Completada' }
    };
    
    const estilo = estilos[estado] || estilos['pendiente'];
    
    return (
      <span style={{
        background: estilo.bg,
        color: estilo.color,
        padding: '0.25rem 0.75rem',
        borderRadius: '1rem',
        fontSize: '0.875rem',
        fontWeight: '500'
      }}>
        {estilo.text}
      </span>
    );
  };

  const openHtmlReport = async (auditId: number) => {
    try {
      // Usar la función del API que ya reparé
      const htmlContent = await auditsApi.getHTMLReport(auditId);
      
      // Crear blob y abrir en nueva ventana
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
    } catch (error) {
      console.error('Error abriendo HTML:', error);
      alert('Error al abrir el reporte HTML');
    }
  };

  const downloadPdfReport = async (auditId: number) => {
    try {
      
      const token = localStorage.getItem('token');
      if (!token) {
        alert('No estás autenticado. Por favor, inicia sesión nuevamente.');
        return;
      }

      // Usar la función del API que ya reparé
      await auditsApi.downloadPDF(auditId);
      
    } catch (error) {
      console.error('Error descargando reporte PDF:', error);
      alert('Error al descargar el reporte PDF');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div>⏳ Cargando auditorías...</div>
      </div>
    );
  }

  if (audits.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <h3 style={{ color: '#6b7280', marginBottom: '1rem' }}>
          📋 No hay auditorías registradas
        </h3>
        <p style={{ color: '#9ca3af', marginBottom: '2rem' }}>
          Solicita tu primera auditoría para comenzar
        </p>
      </div>
    );
  }

  // Calcular totales solo de recibidas
  const totalAuditorias = recibidas.length;
  const totalGasto = recibidas.reduce((sum, a) => sum + parseFloat(String(a.costoFinal || 0)), 0);
  const totalPagado = recibidas.filter(a => a.estadoPago === 'pagada').reduce((sum, a) => sum + parseFloat(String(a.costoFinal || 0)), 0);
  const totalAdeudado = totalGasto - totalPagado;

  return (
    <div>
      {/* Información Importante */}
      <div style={{
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '0.5rem',
        padding: '1rem',
        marginBottom: '1.5rem',
        fontSize: '0.875rem',
        color: '#1e40af'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <span style={{ fontSize: '1.25rem', flexShrink: 0 }}>ℹ️</span>
          <div>
            <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Información Importante</strong>
            <div style={{ lineHeight: '1.6' }}>
              📅 <strong>Retención de auditorías:</strong> Las auditorías completadas se conservan por 30 días. Pasado este período, podrán ser eliminadas del sistema.
              <br />
              💰 <strong>Precios:</strong> Todos los precios mostrados no incluyen IVA.
            </div>
          </div>
        </div>
      </div>

      {/* Resumen General - Solo mostrar si NO es vista de solo pendientes */}
      {!showOnlyPending && (
        <div className="card" style={{ padding: '1.5rem', marginBottom: '2rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <h3 style={{ color: 'white', marginBottom: '1rem', fontSize: '1.5rem' }}>
            📊 Resumen General
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div style={{ background: 'rgba(255,255,255,0.2)', padding: '1rem', borderRadius: '0.5rem' }}>
              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>Auditorías Recibidas</div>
              <div style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold' }}>{totalAuditorias}</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.2)', padding: '1rem', borderRadius: '0.5rem' }}>
              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>Gasto Total</div>
              <div style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold' }}>${totalGasto.toLocaleString()}</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.2)', padding: '1rem', borderRadius: '0.5rem' }}>
              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>Pagado</div>
              <div style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold' }}>${totalPagado.toLocaleString()}</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.2)', padding: '1rem', borderRadius: '0.5rem' }}>
              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>Adeudado</div>
              <div style={{ color: '#fef08a', fontSize: '1.5rem', fontWeight: 'bold' }}>${totalAdeudado.toLocaleString()}</div>
            </div>
          </div>
        </div>
      )}

      {/* Mis Pedidos - Solo mostrar si NO es vista de solo entregadas */}
      {!showOnlyDelivered && misPedidos.length > 0 && (
        <div className="card" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1f2937' }}>
            📤 Mis Pedidos
          </h3>
          <div style={{ display: 'grid', gap: '1rem' }}>
            {misPedidos
              .sort((a, b) => new Date(b.fechaSolicitada).getTime() - new Date(a.fechaSolicitada).getTime())
              .map((audit) => (
              <div
                key={audit.id}
                className="card"
                style={{
                  padding: '1.5rem',
                  borderLeft: '4px solid #f59e0b',
                  background: '#fefce8'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                  <div style={{ flex: '1' }}>
                    <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
                      {audit.titulo}
                    </h4>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      <div>📅 Solicitada: {new Date(audit.fechaSolicitada).toLocaleDateString('es-AR')}</div>
                      <div>💰 Costo: ${(audit.costoFinal || 0).toLocaleString()}</div>
                      <div>🏢 {audit.branch?.nombre || audit.sucursal || 'Sin sucursal'}</div>
                    </div>
                  </div>
                  {audit.estado === 'pendiente' && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => onEditAudit && onEditAudit(audit.id)}
                        style={{
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        ✏️ Editar
                      </button>
                      <button
                        onClick={() => handleDeleteAudit(audit.id)}
                        style={{
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        🗑️ Eliminar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Auditorías Recibidas por Sucursal - Solo mostrar si NO es vista de solo pendientes */}
      {!showOnlyPending && recibidas.length > 0 && (
        <div>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1f2937' }}>
            📥 Auditorías Recibidas
          </h3>
          {Object.entries(
            recibidas.reduce((acc, audit) => {
              const sucursalNombre = audit.branch?.nombre || audit.sucursal || 'Sin sucursal';
              if (!acc[sucursalNombre]) {
                acc[sucursalNombre] = [];
              }
              acc[sucursalNombre].push(audit);
              return acc;
            }, {} as Record<string, Audit[]>)
          ).map(([sucursalNombre, sucursalAudits]) => {
        const sucursalTotal = sucursalAudits.reduce((sum, a) => sum + parseFloat(String(a.costoFinal || 0)), 0);
        const sucursalPagado = sucursalAudits.filter(a => a.estadoPago === 'pagada').reduce((sum, a) => sum + parseFloat(String(a.costoFinal || 0)), 0);
        const sucursalAdeudado = sucursalTotal - sucursalPagado;
        const sucursalEntregadas = sucursalAudits.filter(a => a.estado === 'entregada' || a.estado === 'facturada').length;

        return (
          <div key={sucursalNombre} style={{ marginBottom: '2rem' }}>
            {/* Encabezado de Sucursal */}
            <div className="card" style={{ padding: '1rem', marginBottom: '1rem', background: '#f3f4f6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <h3 style={{ color: '#1f2937', fontSize: '1.25rem', fontWeight: '600' }}>
                  🏢 {sucursalNombre}
                </h3>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.875rem' }}>
                  <div>
                    <span style={{ color: '#6b7280' }}>Auditorías: </span>
                    <strong style={{ color: '#1f2937' }}>{sucursalAudits.length}</strong>
                    <span style={{ color: '#10b981', marginLeft: '0.25rem' }}>({sucursalEntregadas} entregadas)</span>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280' }}>Total: </span>
                    <strong style={{ color: '#1f2937' }}>${sucursalTotal.toLocaleString()}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280' }}>Pagado: </span>
                    <strong style={{ color: '#10b981' }}>${sucursalPagado.toLocaleString()}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#6b7280' }}>Adeudado: </span>
                    <strong style={{ color: '#ef4444' }}>${sucursalAdeudado.toLocaleString()}</strong>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de Auditorías de la Sucursal */}
            <div style={{ display: 'grid', gap: '1rem' }}>
              {sucursalAudits
                .sort((a, b) => {
                  // Ordenar por fecha de aprobación/entrega (más recientes primero)
                  const dateA = new Date(a.fechaAprobacion || a.fechaTerminacion || a.fechaSolicitada).getTime();
                  const dateB = new Date(b.fechaAprobacion || b.fechaTerminacion || b.fechaSolicitada).getTime();
                  return dateB - dateA; // Descendente (más nuevas arriba)
                })
                .map((audit) => (
                <div
                  key={audit.id}
                  className="card"
                  style={{
                    padding: '1.5rem',
                    borderLeft: audit.estado === 'entregada' ? '4px solid #10b981' : '4px solid #f59e0b'
                  }}
                >
                  <div style={{ marginBottom: '1rem' }}>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
                      {audit.titulo}
                    </h4>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      <div style={{ marginBottom: '0.25rem' }}>
                        <span>📅 Entregada: <strong style={{ color: '#1f2937' }}>
                          {audit.fechaAprobacion
                            ? new Date(audit.fechaAprobacion).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : audit.fechaTerminacion
                              ? new Date(audit.fechaTerminacion).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                              : 'Pendiente'}
                        </strong></span>
                      </div>
                      <div>
                        <span>💰 Costo: <strong style={{ color: '#1f2937' }}>${(audit.costoFinal || 0).toLocaleString()}</strong></span>
                      </div>
                    </div>
                  </div>

                  {(audit.estado === 'entregada' || audit.estado === 'terminada') && (
                    <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => openHtmlReport(audit.id)}
                        style={{
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 1.25rem',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        🌐 Ver Reporte HTML
                      </button>
                      <button
                        onClick={() => downloadPdfReport(audit.id)}
                        style={{
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 1.25rem',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        📄 Descargar PDF
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
        </div>
      )}
    </div>
  );
};

export default ClientAuditsList;